import React, { useEffect, useState } from 'react'
import { Button, Collapse, Input, Modal, Typography } from 'antd'
import PropTypes from 'prop-types'

import { ImageUploader } from '~components'
import { MultiSelectDropdown } from '~stories'

import {
  MAX_VENDOR_FILTER_SELECTIONS,
  getDefaultConcessionVendor,
  normalizeConcessionVendor
} from '../../utils/concessionVendorGridDefaults'
import {
  buildVendorSectionLabel
} from '../../utils/concessionVendorLocationFilters'
import { ConcessionBadgeFields } from '../ConcessionGalleryItems'
import styles from '../FAQPageSections/FAQAdminFields.module.css'
import DownArrow from '../PositionArrows/DownArrow'
import UpArrow from '../PositionArrows/UpArrow'
import ConcessionVendorCardPreview from './ConcessionVendorCardPreview'
import editorStyles from './ConcessionVendorGridEditor.module.css'
import ConcessionVendorLocationFilter from './ConcessionVendorLocationFilter'

const { Panel } = Collapse
const { Text } = Typography
const { TextArea } = Input

const getVendorLabel = (vendor, index) =>
  vendor?.name?.trim() || `Vendor ${index + 1}`

const ConcessionVendorGridEditor = ({
  cuisineFilterOptions = [],
  cuisinePlaceholder = 'Select cuisine',
  dietaryFilterOptions = [],
  dietaryPlaceholder = 'Select dietary preferences',
  hasVenue = false,
  itemErrors = [],
  locationFilterLevels = [],
  locationPlaceholder = 'All Levels',
  vendors = [],
  onChange
}) => {
  const [activeKey, setActiveKey] = useState(vendors.length ? '0' : undefined)

  useEffect(() => {
    if (!vendors.length) {
      setActiveKey(undefined)
      return
    }

    setActiveKey(prev => {
      const activeIndex = Number(prev)
      if (Number.isNaN(activeIndex) || activeIndex >= vendors.length) {
        return String(vendors.length - 1)
      }
      return prev
    })
  }, [vendors.length])

  useEffect(() => {
    const firstErrorIndex = itemErrors.findIndex(itemError =>
      Object.values(itemError || {}).some(Boolean)
    )

    if (firstErrorIndex >= 0) {
      setActiveKey(String(firstErrorIndex))
    }
  }, [itemErrors])

  const updateVendors = nextVendors => onChange(nextVendors.map(normalizeConcessionVendor))

  useEffect(() => {
    if (!locationFilterLevels.length || !vendors.length) {
      return
    }

    let changed = false
    const nextVendors = vendors.map(vendor => {
      const nextSectionLabel = buildVendorSectionLabel(
        vendor.section_label,
        vendor.filters?.location,
        locationFilterLevels
      )

      if (nextSectionLabel === String(vendor.section_label ?? '').trim()) {
        return vendor
      }

      changed = true
      return { ...vendor, section_label: nextSectionLabel }
    })

    if (changed) {
      updateVendors(nextVendors)
    }
  // Recompute stored labels once venue location filters load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationFilterLevels])

  const updateVendor = (index, partial) => {
    updateVendors(vendors.map((vendor, vendorIndex) => (
      vendorIndex === index ? { ...vendor, ...partial } : vendor
    )))
  }

  const updateVendorFilters = (index, filterPartial) => {
    const vendor = vendors[index] || {}
    const nextFilters = {
      ...(vendor.filters || {}),
      ...filterPartial
    }

    updateVendor(index, {
      filters: nextFilters,
      section_label: buildVendorSectionLabel(
        vendor.section_label,
        nextFilters.location,
        locationFilterLevels
      )
    })
  }

  const handleBadgeChange = (index, badgeValue) => {
    updateVendor(index, { badge: badgeValue })
  }

  const handleAddVendor = () => {
    const nextVendors = [...vendors, getDefaultConcessionVendor()]
    updateVendors(nextVendors)
    setActiveKey(String(nextVendors.length - 1))
  }

  const handleDeleteVendor = index => {
    const vendorName = getVendorLabel(vendors[index], index)
    Modal.confirm({
      title: 'Delete vendor?',
      content: `Are you sure you want to delete "${vendorName}"? This change will apply when you save the page.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        const nextVendors = vendors.filter((_, vendorIndex) => vendorIndex !== index)
        updateVendors(nextVendors)
        if (!nextVendors.length) {
          setActiveKey(undefined)
          return
        }
        const nextIndex = Math.min(index, nextVendors.length - 1)
        setActiveKey(String(nextIndex))
      }
    })
  }

  const handleMoveUp = (event, index) => {
    event.stopPropagation()
    if (index <= 0) {
      return
    }
    const nextVendors = [...vendors]
    ;[nextVendors[index - 1], nextVendors[index]] = [nextVendors[index], nextVendors[index - 1]]
    updateVendors(nextVendors)
    setActiveKey(String(index - 1))
  }

  const handleMoveDown = (event, index) => {
    event.stopPropagation()
    if (index >= vendors.length - 1) {
      return
    }
    const nextVendors = [...vendors]
    ;[nextVendors[index + 1], nextVendors[index]] = [nextVendors[index], nextVendors[index + 1]]
    updateVendors(nextVendors)
    setActiveKey(String(index + 1))
  }

  const previewIndex = activeKey != null ? Number(activeKey) : 0
  const previewVendor = vendors.length
    ? (vendors[previewIndex] || vendors[0])
    : null

  return (
    <div className={editorStyles.editorLayout}>
      <div className={editorStyles.editorMain}>
        <div className={styles.toolbar}>
          <Text className={styles.toolbarMeta}>
            {`${vendors.length} vendor(s) configured.`}
          </Text>
          <Button onClick={handleAddVendor} type="primary">
            Add Vendor
          </Button>
        </div>

        {vendors.length ? (
          <Collapse
            accordion
            activeKey={activeKey}
            expandIconPosition="right"
            onChange={key => setActiveKey(key === undefined ? undefined : String(key))}
          >
            {vendors.map((vendor, index) => {
              const errors = itemErrors[index] || {}

              return (
                <Panel
                  key={String(index)}
                  extra={(
                    <div className={styles.panelActions} onClick={event => event.stopPropagation()}>
                      {index > 0 && (
                        <span data-testid="vendor-up-arrow" onClick={event => handleMoveUp(event, index)}>
                          <UpArrow />
                        </span>
                      )}
                      {index < vendors.length - 1 && (
                        <span data-testid="vendor-down-arrow" onClick={event => handleMoveDown(event, index)}>
                          <DownArrow />
                        </span>
                      )}
                      <Button danger onClick={() => handleDeleteVendor(index)} size="small">
                        Delete
                      </Button>
                    </div>
                  )}
                  header={getVendorLabel(vendor, index)}
                >
                  <div className={styles.fieldRow}>
                    <Text className={`${styles.fieldLabel}${errors.name ? ` ${styles.fieldLabelError}` : ''}`}>Header</Text>
                    <div className={errors.name ? styles.fieldControlError : undefined}>
                      <Input
                        onChange={event => updateVendor(index, { name: event.target.value })}
                        placeholder="Courtside/Rinkside Bar"
                        status={errors.name ? 'error' : undefined}
                        value={vendor.name || ''}
                      />
                    </div>
                    {errors.name && <Text className={styles.fieldError}>{errors.name}</Text>}
                  </div>

                  <div className={styles.fieldRow}>
                    <Text className={`${styles.fieldLabel}${errors.image ? ` ${styles.fieldLabelError}` : ''}`}>Image</Text>
                    <div className={errors.image ? styles.fieldControlError : undefined}>
                      <ImageUploader
                        alt={vendor.image?.alt || ''}
                        handleIconChange={({ alt, url }) => updateVendor(index, {
                          image: {
                            ...(vendor.image || {}),
                            url: url || vendor.image?.url || '',
                            alt: alt ?? vendor.image?.alt ?? ''
                          }
                        })}
                        handleUpload={url => updateVendor(index, {
                          image: {
                            ...(vendor.image || {}),
                            url: url || ''
                          }
                        })}
                        logoUrl={vendor.image?.url || vendor.image?.src || ''}
                        pageType="concession"
                      />
                    </div>
                    {errors.image && <Text className={styles.fieldError}>{errors.image}</Text>}
                  </div>

                  <div className={styles.fieldRow}>
                    <Text className={styles.fieldLabel}>Image Alt Text</Text>
                    <Input
                      onChange={event => updateVendor(index, {
                        image: {
                          ...(vendor.image || {}),
                          url: vendor.image?.url || vendor.image?.src || '',
                          alt: event.target.value
                        }
                      })}
                      placeholder="Describe the vendor image for accessibility"
                      value={vendor.image?.alt || ''}
                    />
                  </div>

                  <ConcessionBadgeFields
                    badge={vendor.badge}
                    onChange={badgeValue => handleBadgeChange(index, badgeValue)}
                  />

                  <div className={styles.fieldRow}>
                    <Text className={`${styles.fieldLabel}${errors.description ? ` ${styles.fieldLabelError}` : ''}`}>Description</Text>
                    <div className={errors.description ? styles.fieldControlError : undefined}>
                      <TextArea
                        onChange={event => updateVendor(index, { description: event.target.value })}
                        placeholder={'BEERS & COCKTAILS\nSection 101'}
                        rows={3}
                        status={errors.description ? 'error' : undefined}
                        value={vendor.description || ''}
                      />
                    </div>
                    {errors.description && <Text className={styles.fieldError}>{errors.description}</Text>}
                  </div>

                  <div className={styles.fieldRow}>
                    <Text className={`${styles.fieldLabel}${errors.location ? ` ${styles.fieldLabelError}` : ''}`}>Location</Text>
                    <div className={errors.location ? styles.fieldControlError : undefined}>
                      <ConcessionVendorLocationFilter
                        hasVenue={hasVenue}
                        locationError={errors.location}
                        locationLevels={locationFilterLevels}
                        locationPlaceholder={locationPlaceholder}
                        onChange={locationKeys => updateVendorFilters(index, {
                          location: locationKeys
                        })}
                        resetKey={index}
                        selectedLocationKeys={vendor.filters?.location || []}
                      />
                    </div>
                    {errors.location && <Text className={styles.fieldError}>{errors.location}</Text>}
                  </div>

                  <div className={styles.fieldRow}>
                    <Text className={`${styles.fieldLabel}${errors.cuisine ? ` ${styles.fieldLabelError}` : ''}`}>Cuisine</Text>
                    {!hasVenue && (
                      <Text type="secondary">Select a page venue above to load cuisine options.</Text>
                    )}
                    {hasVenue && !cuisineFilterOptions.length && (
                      <Text type="secondary">No cuisine options configured for this venue.</Text>
                    )}
                    <div className={errors.cuisine ? styles.fieldControlError : undefined}>
                      <MultiSelectDropdown
                        allowClear
                        disabled={!hasVenue || !cuisineFilterOptions.length}
                        displayField="label"
                        dropdownOptions={hasVenue ? cuisineFilterOptions : []}
                        handleChange={keys => {
                          const selected = keys || []
                          if (selected.length > MAX_VENDOR_FILTER_SELECTIONS) return
                          updateVendorFilters(index, { cuisine: selected })
                        }}
                        mode="multiple"
                        placeholder={cuisinePlaceholder}
                        selectedItems={vendor.filters?.cuisine || []}
                        valueField="key"
                      />
                    </div>
                    {errors.cuisine
                      ? <Text className={styles.fieldError}>{errors.cuisine}</Text>
                      : <Text style={{ color: '#8c8c8c', display: 'block', fontSize: 12, marginTop: 4 }}>
                        Maximum {MAX_VENDOR_FILTER_SELECTIONS} selections
                      </Text>
                    }
                  </div>

                  <div className={styles.fieldRow}>
                    <Text className={`${styles.fieldLabel}${errors.dietary_preferences ? ` ${styles.fieldLabelError}` : ''}`}>Dietary Preferences</Text>
                    {!hasVenue && (
                      <Text type="secondary">Select a page venue above to load dietary options.</Text>
                    )}
                    {hasVenue && !dietaryFilterOptions.length && (
                      <Text type="secondary">No dietary options configured for this venue.</Text>
                    )}
                    <div className={errors.dietary_preferences ? styles.fieldControlError : undefined}>
                      <MultiSelectDropdown
                        allowClear
                        disabled={!hasVenue || !dietaryFilterOptions.length}
                        displayField="label"
                        dropdownOptions={hasVenue ? dietaryFilterOptions : []}
                        handleChange={keys => {
                          const selected = keys || []
                          if (selected.length > MAX_VENDOR_FILTER_SELECTIONS) return
                          updateVendorFilters(index, { dietary_preferences: selected })
                        }}
                        mode="multiple"
                        placeholder={dietaryPlaceholder}
                        selectedItems={vendor.filters?.dietary_preferences || []}
                        valueField="key"
                      />
                    </div>
                    {errors.dietary_preferences
                      ? <Text className={styles.fieldError}>{errors.dietary_preferences}</Text>
                      : <Text style={{ color: '#8c8c8c', display: 'block', fontSize: 12, marginTop: 4 }}>
                        Maximum {MAX_VENDOR_FILTER_SELECTIONS} selections
                      </Text>
                    }
                  </div>
                </Panel>
              )
            })}
          </Collapse>
        ) : (
          <div className={styles.emptyState}>
            <Text type="secondary">No vendors configured yet. Add a vendor to get started.</Text>
          </div>
        )}
      </div>

      <div className={editorStyles.editorPreview}>
        <ConcessionVendorCardPreview vendor={previewVendor} />
      </div>
    </div>
  )
}

ConcessionVendorGridEditor.propTypes = {
  cuisineFilterOptions: PropTypes.array,
  cuisinePlaceholder: PropTypes.string,
  dietaryFilterOptions: PropTypes.array,
  dietaryPlaceholder: PropTypes.string,
  hasVenue: PropTypes.bool,
  itemErrors: PropTypes.array,
  locationFilterLevels: PropTypes.array,
  locationPlaceholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  vendors: PropTypes.array
}

export default ConcessionVendorGridEditor
